import React, { useCallback, useId, useState } from 'react'

import {
  Check, ClosingDropdown, Icon, classnames,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, bool, func, number, shape, string,
} from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import formatPrice from 'Utils/formatPrice'
import { NOOP } from 'Utils/stableDefaults'

const TRANSLATION_KEY_PREFIX = 'subscription_checkout.plans_restructure.billing_term'
const CHECK_SIZE = { width: '14', height: '14' }

function BillingTerm({
  plans, selectedKey = null, onChange = NOOP, isChangeDisabled = false, locale = 'en',
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [shouldClose, setShouldClose] = useState(false)
  const menuId = useId()

  const translate = useCallback(
    (key, options = {}) => I18n.t(`${TRANSLATION_KEY_PREFIX}.${key}`, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  const selectedPlan = plans.find((plan) => plan.key === selectedKey) || plans[0]
  const hasChoice = plans.length > 1
  const priceLabel = (plan) => translate(`price.${plan.key}`, { price: formatPrice(plan.price) })

  const handlePick = (planKey) => {
    setShouldClose(true)
    onChange(planKey)
  }

  const handleMenuKeyDown = (event) => {
    if (event.key === 'Escape') setShouldClose(true)
  }

  const menu = (
    <div
      id={menuId}
      role="group"
      aria-label={translate('title')}
      className="billing-term__options"
      onKeyDown={handleMenuKeyDown}
    >
      <div className="billing-term__title cgt-overline-sm" aria-hidden="true">
        {translate('title')}
      </div>
      {plans.map((plan) => {
        const isSelected = plan.key === selectedPlan.key
        const isUnavailable = plan.isAvailable === false

        return (
          <button
            key={plan.key}
            type="button"
            aria-pressed={isSelected}
            className={classnames(
              'billing-term__option',
              isSelected && 'billing-term__option--selected',
              isUnavailable && 'billing-term__option--unavailable',
            )}
            disabled={isUnavailable}
            onClick={() => handlePick(plan.key)}
          >
            <span className="billing-term__check">
              {isSelected && <Icon svgComponent={Check} color="primary" size={CHECK_SIZE} notSpaced />}
            </span>
            <span className="billing-term__option-text">
              <span className="billing-term__option-header">
                <span className="billing-term__option-name cgt-body-base-semibold">
                  {translate(`plans.${plan.key}.name`)}
                </span>
                {plan.freeMonths > 0 && (
                  <span className="billing-term__badge cgt-caption-semibold">
                    {translate('free_months', { months: plan.freeMonths })}
                  </span>
                )}
              </span>
              <span className="billing-term__option-note cgt-caption">
                {translate(`plans.${plan.key}.note`)}
              </span>
            </span>
            <span className="billing-term__option-price cgt-body-base-semibold">
              {priceLabel(plan)}
            </span>
          </button>
        )
      })}
    </div>
  )

  return (
    <div className="billing-term cgt-caption">
      {translate('summary', {
        plan: translate(`plans.${selectedPlan.key}.name`),
        price: priceLabel(selectedPlan),
        term: translate(`plans.${selectedPlan.key}.term`),
      })}
      {hasChoice && (
        <>
          {' '}
          <ClosingDropdown
            content={menu}
            isSimpleContent
            isDisabled={isChangeDisabled}
            placement="bottom-end"
            offset={8}
            onOpenChange={setIsOpen}
            triggerCloseDropdown={shouldClose}
            setTriggerCloseDropdown={setShouldClose}
            className="billing-term__dropdown"
            contentClassName="billing-term__menu"
          >
            <button
              type="button"
              className="billing-term__change cgt-caption-semibold"
              disabled={isChangeDisabled}
              aria-haspopup="true"
              aria-expanded={isOpen}
              aria-controls={isOpen ? menuId : undefined}
            >
              {translate('change')}
            </button>
          </ClosingDropdown>
        </>
      )}
    </div>
  )
}

BillingTerm.propTypes = {
  plans: arrayOf(shape({
    key: string.isRequired,
    price: number,
    freeMonths: number,
    isAvailable: bool,
  })).isRequired,
  selectedKey: string,
  onChange: func,
  isChangeDisabled: bool,
  locale: string,
}

export default BillingTerm
